import {
  getPercentageChange,
  getPreviousYearMetrics,
} from '@app/purchased-goods/insights/lib/insights-yoy';
import { useInsightsPreviousPeriod } from '@app/purchased-goods/insights/hooks/use-insights-previous-period';
import {
  EmissionsCategoryEnum,
  PurchasedGoodItemInsights,
} from '@shared/api/types';
import SmallNumber from '@shared/components/content/small-number';
import { DashboardGrid } from '@shared/components/dashboard/dashboard-grid';
import { MetricCard } from '@shared/components/dashboard/metric-card';
import { MetricTrend } from '@shared/components/dashboard/metric-trend';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import { formatCompact, getCurrencySymbol } from '@shared/lib/format';
import { formatSmallNumber } from '@shared/lib/utils';

// Placeholder copy — final wording lands in the project's copy pass.
const COST_UPSELL = 'Unlock combined spend + carbon insights';
const ADD_SPEND = 'Add spend data to unlock cost + carbon insights';

type Props = {
  data?: PurchasedGoodItemInsights;
  /** The item's carbon rating; omitted for packaging, which has none. */
  rating?: EmissionsCategoryEnum | null;
  currency?: string | null;
  /** Whether the org has the cost feature; false → spend cards render locked. */
  hasCostFeature: boolean;
  /** Link target for the Spend card's drill-through. */
  spendHref?: string;
  isLoading?: boolean;
};

const formatCompactSmall = (value: number) => {
  if (value === 0) return '0';
  return value >= 1000 ? formatCompact(value) : formatSmallNumber(value);
};

/**
 * The item's four headline metrics: Emissions, Carbon intensity, Spend, and
 * Emissions per spend, computed for the page's period and site scope with
 * year-on-year comparison badges. The two spend-dependent cards render locked
 * (with an upsell) when the org lacks the cost feature, and blank with an
 * "add spend data" prompt when the feature is on but no spend was shared.
 */
export function ItemKpiRow({
  data,
  rating,
  currency,
  hasCostFeature,
  spendHref,
  isLoading,
}: Props) {
  const symbol = getCurrencySymbol(currency) || '£';

  const emissions = data?.emissionsTonnes ?? null;
  const emissionsPerKg = data?.emissionsPerKg ?? null;
  const spend = data?.spend != null ? Number(data.spend) : null;
  const emissionsPerSpend = data?.emissionsPerSpendUnit ?? null;
  const weightKg = data?.weightKg ?? null;

  // "Add spend data" only when the window HAS purchases but none carry spend —
  // an empty window is not missing data, so it stays a plain dash.
  const noSpendData = hasCostFeature && spend == null && emissions != null;

  const previousYear = getPreviousYearMetrics(data?.previousYear);
  const { name: previousPeriodName, spansMultipleYears } =
    useInsightsPreviousPeriod(data?.timePeriodStart, data?.timePeriodEnd);
  const comparisonLabel =
    previousPeriodName && !spansMultipleYears
      ? `Compared with the same months in ${previousPeriodName}`
      : null;

  const yearOnYear = (
    current: number | null,
    previous: number | null,
    variant: 'impact' | 'neutral' = 'impact'
  ) => {
    if (!comparisonLabel) return undefined;
    const percentChange = getPercentageChange(current, previous);
    if (percentChange == null) return undefined;
    return (
      <MetricTrend
        percentChange={percentChange}
        label={comparisonLabel}
        variant={variant}
      />
    );
  };

  return (
    <DashboardGrid className="lg:grid-cols-4">
      <MetricCard
        variant="default"
        title="Emissions"
        value={
          isLoading || emissions == null ? undefined : (
            <SmallNumber value={emissions} formatter={formatCompactSmall} />
          )
        }
        unit="tCO₂e"
        description={
          weightKg != null
            ? `From ${formatCompactSmall(weightKg / 1000)} tonnes purchased`
            : undefined
        }
        headerAction={yearOnYear(emissions, previousYear.emissionsTonnes)}
      />
      <MetricCard
        variant="default"
        title="Carbon intensity"
        value={
          isLoading || emissionsPerKg == null ? undefined : (
            <span className="inline-flex items-center gap-2">
              {rating ? (
                <RatingCloud rating={rating} className="h-7 w-auto" />
              ) : null}
              <SmallNumber value={emissionsPerKg} />
            </span>
          )
        }
        unit="kgCO₂e/kg"
        description="Emissions per kg purchased"
        headerAction={yearOnYear(emissionsPerKg, previousYear.emissionsPerKg)}
      />
      <MetricCard
        variant="default"
        title="Spend"
        locked={!hasCostFeature}
        lockedDescription={COST_UPSELL}
        value={
          isLoading || spend == null
            ? undefined
            : spend > 0 && spend < 1
              ? `<${symbol}1`
              : `${symbol}${formatCompact(spend)}`
        }
        description={noSpendData ? ADD_SPEND : undefined}
        footerLink={
          spendHref && !noSpendData
            ? { label: 'View spend insights', to: spendHref }
            : undefined
        }
        // Spend rising or falling is not itself good or bad, so it stays muted.
        headerAction={yearOnYear(spend, previousYear.spend, 'neutral')}
      />
      <MetricCard
        variant="default"
        title="Emissions per spend"
        locked={!hasCostFeature}
        lockedDescription={COST_UPSELL}
        value={
          isLoading || emissionsPerSpend == null ? undefined : (
            <SmallNumber value={emissionsPerSpend} />
          )
        }
        unit={`kgCO₂e/${symbol}`}
        description={noSpendData ? ADD_SPEND : `Emissions per ${symbol} spent`}
        headerAction={yearOnYear(
          emissionsPerSpend,
          previousYear.emissionsPerSpendUnit
        )}
      />
    </DashboardGrid>
  );
}
